<script setup>
import { ref, computed, nextTick, onBeforeUnmount, watch } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import ContactAPI from 'dashboard/api/contacts';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import {
  isDuplicatePhoneError,
  isInvalidPhoneError,
} from 'shared/helpers/contactError';
import { useContactBulkActions } from 'dashboard/composables/useContactBulkActions';
import ContactsCard from 'dashboard/components-next/Contacts/ContactsCard/ContactsCard.vue';
import ContactBulkLabelActions from 'dashboard/components-next/Contacts/ContactBulkLabelActions/ContactBulkLabelActions.vue';
import FollowUpEnrollDialog from 'dashboard/components-next/Contacts/FollowUpEnrollDialog/FollowUpEnrollDialog.vue';
import FollowUpEditDialog from 'dashboard/components-next/Contacts/FollowUpEnrollDialog/FollowUpEditDialog.vue';
import FollowUpEnrollmentsAPI from 'dashboard/api/followUpEnrollments';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import WhatsappFlowEnroll from 'dashboard/components/widgets/conversation/WhatsappFlowEnroll.vue';

const props = defineProps({
  contacts: { type: Array, required: true },
  totalContacts: { type: Number, default: 0 },
  sortAttr: { type: String, default: 'name' },
});

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();

const uiFlags = useMapGetter('contacts/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);
const expandedCardId = ref(null);
const showFlowEnrollModal = ref(false);
const selectedContactId = ref(null);
const hoveredCardId = ref(null);
const showLabelActions = ref(false);
const labelButtonRef = ref(null);
const dropdownStyle = ref({ top: '0px', left: '0px' });

const {
  selectedContactIds,
  hasSelected,
  selectedCount,
  isApplying,
  isFetchingAll,
  toggleContact,
  selectAll,
  selectAllIds,
  clearSelection,
  updateLabels,
  deleteContacts,
} = useContactBulkActions();

const deleteDialogRef = ref(null);

const openDeleteDialog = () => {
  if (!hasSelected.value) return;
  deleteDialogRef.value?.open();
};

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);
const isFollowUpEnabled = computed(() =>
  isFeatureEnabledonAccount.value(
    currentAccountId.value,
    FEATURE_FLAGS.CONVERSATION_FOLLOW_UPS
  )
);

const followUpEnrollDialogRef = ref(null);
const followUpContactIds = ref([]);

// Per-contact in-progress cycle of the page on screen ({contactId: row}),
// so each card can say "Adicionado a <fluxo>" instead of offering a second
// enrollment. Refreshed on page change and after any dialog action.
const followUpByContactId = ref({});

// False while the map cannot be trusted: a new page not yet fetched, or a
// mutation that invalidated what was known. The cards withhold BOTH actions
// until a fetch brings the truth back — never act on state the UI itself
// knows to be stale.
const followUpStateKnown = ref(false);

// Identity of the lookup on screen: only the CURRENT generation may apply
// its response, so a slow page-A lookup can never overwrite the page-B rows
// that arrived after it. A new id set also drops the previous page's map.
let followUpLookupGeneration = 0;
let followUpLookupKey = '';

// The unknown state needs an exit: a failed lookup retries by itself (short
// backoff), and if every attempt fails a visible "try again" takes over —
// the page must never lose its follow-up controls silently.
const FOLLOW_UP_RETRY_DELAYS_MS = [2000, 6000];
const followUpLookupFailed = ref(false);
let followUpRetryTimer = null;

const clearFollowUpRetry = () => {
  clearTimeout(followUpRetryTimer);
  followUpRetryTimer = null;
};

onBeforeUnmount(clearFollowUpRetry);

// `invalidate` marks a refresh caused by a mutation (enroll, pause, remove,
// move): what the map says about this page is no longer true, so it is
// dropped BEFORE the fetch — even though the id set did not change.
const refreshFollowUps = async ({ invalidate = false, attempt = 0 } = {}) => {
  if (!isFollowUpEnabled.value) return;
  clearFollowUpRetry();
  followUpLookupGeneration += 1;
  const generation = followUpLookupGeneration;
  if (attempt === 0) followUpLookupFailed.value = false;
  const contactIds = props.contacts.map(contact => contact.id);
  const key = contactIds.join(',');
  if (invalidate || key !== followUpLookupKey) {
    followUpLookupKey = key;
    followUpByContactId.value = {};
    followUpStateKnown.value = false;
  }
  if (!contactIds.length) {
    followUpStateKnown.value = true;
    return;
  }
  try {
    const { data } = await FollowUpEnrollmentsAPI.lookup(contactIds);
    if (generation !== followUpLookupGeneration) return;
    // Rows come ordered by id: on the rare contact with more than one
    // in-progress cycle, the most recent wins the card.
    followUpByContactId.value = (data.enrollments || []).reduce(
      (acc, row) => ({ ...acc, [row.contact_id]: row }),
      {}
    );
    followUpStateKnown.value = true;
    followUpLookupFailed.value = false;
  } catch {
    // the state stays unknown: better no action than a stale one
    if (generation !== followUpLookupGeneration) return;
    if (attempt < FOLLOW_UP_RETRY_DELAYS_MS.length) {
      followUpRetryTimer = setTimeout(
        () => refreshFollowUps({ attempt: attempt + 1 }),
        FOLLOW_UP_RETRY_DELAYS_MS[attempt]
      );
    } else {
      followUpLookupFailed.value = true;
    }
  }
};

const refreshFollowUpsAfterMutation = () =>
  refreshFollowUps({ invalidate: true });

const retryFollowUpLookup = () => refreshFollowUps();

watch(
  () => props.contacts,
  () => refreshFollowUps(),
  { immediate: true }
);

const followUpEditDialogRef = ref(null);
const editingFollowUp = ref(null);

const handleEditFollowUp = async contactId => {
  editingFollowUp.value = followUpByContactId.value[contactId] || null;
  if (!editingFollowUp.value) return;
  // the dialog reads the enrollment prop on open — let it propagate first
  await nextTick();
  followUpEditDialogRef.value?.open();
};

const handleAddToFollowUp = contactId => {
  followUpContactIds.value = [contactId];
  followUpEnrollDialogRef.value?.open();
};

const openBulkFollowUp = () => {
  if (!hasSelected.value) return;
  followUpContactIds.value = Array.from(selectedContactIds.value);
  followUpEnrollDialogRef.value?.open();
};

const handleBulkDelete = async () => {
  const success = await deleteContacts();
  if (success) {
    deleteDialogRef.value?.close();
  }
};

const allSelected = computed(
  () =>
    props.contacts.length > 0 &&
    props.contacts.every(c => selectedContactIds.value.has(c.id))
);

const isIndeterminate = computed(() => hasSelected.value && !allSelected.value);

const bulkCheckboxValue = computed({
  get: () => allSelected.value,
  set: shouldSelectAll => {
    if (shouldSelectAll) selectAll(props.contacts);
    else clearSelection();
  },
});

watch(
  [
    () => route.query.page,
    () => route.query.search,
    () => route.params.label,
    () => route.params.segmentId,
  ],
  () => {
    if (hasSelected.value) clearSelection();
  }
);

const showSelectAllBanner = computed(
  () => allSelected.value && selectedCount.value < props.totalContacts
);

const selectAllInView = async () => {
  isFetchingAll.value = true;
  try {
    const label = route.params.label || '';
    const search = route.query.search || '';
    const { data } = await ContactAPI.getIds(props.sortAttr, label, search);
    selectAllIds(data.ids);
  } catch {
    useAlert(t('BULK_ACTION.CONTACTS.SELECT_ALL_FAILED'));
  } finally {
    isFetchingAll.value = false;
  }
};

const selectableCardIds = computed(() => {
  if (hasSelected.value) return new Set(props.contacts.map(c => c.id));
  return new Set(hoveredCardId.value ? [hoveredCardId.value] : []);
});

const openLabelActions = async () => {
  await nextTick();
  const el = labelButtonRef.value?.$el ?? labelButtonRef.value;
  if (el) {
    const rect = el.getBoundingClientRect();
    dropdownStyle.value = {
      top: `${rect.bottom + 8}px`,
      left: `${rect.left}px`,
    };
  }
  showLabelActions.value = true;
};

const handleApplyLabels = async ({ labels, mode }) => {
  showLabelActions.value = false;
  await updateLabels({ labels, mode });
};

const updateContact = async updatedData => {
  try {
    await store.dispatch('contacts/update', updatedData);
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
  } catch (error) {
    const i18nPrefix = 'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.FORM';
    if (isDuplicatePhoneError(error)) {
      useAlert(t(`${i18nPrefix}.PHONE_NUMBER.DUPLICATE`));
    } else if (isInvalidPhoneError(error)) {
      useAlert(t('PHONE_INPUT.ERROR'));
    } else if (
      error instanceof DuplicateContactException &&
      error.data.includes('email')
    ) {
      useAlert(t(`${i18nPrefix}.EMAIL_ADDRESS.DUPLICATE`));
    } else if (error instanceof ExceptionWithMessage) {
      useAlert(error.data);
    } else {
      useAlert(t(`${i18nPrefix}.ERROR_MESSAGE`));
    }
  }
};

const onClickViewDetails = async id => {
  const routeTypes = {
    contacts_dashboard_segments_index: ['contacts_edit_segment', 'segmentId'],
    contacts_dashboard_labels_index: ['contacts_edit_label', 'label'],
  };
  const [name, paramKey] = routeTypes[route.name] || ['contacts_edit'];
  const params = {
    contactId: id,
    ...(paramKey && { [paramKey]: route.params[paramKey] }),
  };

  await router.push({ name, params, query: route.query });
};

const toggleExpanded = id => {
  expandedCardId.value = expandedCardId.value === id ? null : id;
};

const handleAddToFlow = contactId => {
  selectedContactId.value = contactId;
  showFlowEnrollModal.value = true;
};

const closeFlowEnrollModal = () => {
  showFlowEnrollModal.value = false;
  selectedContactId.value = null;
};
</script>

<template>
  <div class="relative isolate flex flex-col gap-4 px-6 pt-4 pb-6">
    <transition
      enter-active-class="transition-all duration-150 ease-out"
      leave-active-class="transition-all duration-150 ease-out"
      enter-from-class="opacity-0 -translate-y-1"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <div
        v-if="hasSelected"
        class="sticky top-0 z-[100] flex items-center gap-3 px-4 py-2.5 rounded-lg bg-n-solid-2 border border-solid border-n-weak shadow-sm"
      >
        <div class="flex items-center gap-2">
          <Checkbox
            v-model="bulkCheckboxValue"
            :indeterminate="isIndeterminate"
          />
          <span class="text-sm font-medium text-n-slate-12 tabular-nums">
            {{ t('BULK_ACTION.CONTACTS.SELECTED', { count: selectedCount }) }}
          </span>
        </div>

        <div class="h-4 w-px bg-n-strong" />

        <div class="flex items-center gap-2">
          <Button
            ref="labelButtonRef"
            sm
            ghost
            slate
            icon="i-lucide-tags"
            :label="t('BULK_ACTION.CONTACTS.LABELS.ASSIGN_LABELS')"
            :is-loading="isApplying"
            class="!px-2"
            @click="openLabelActions"
          />
        </div>

        <template v-if="isFollowUpEnabled">
          <div class="h-4 w-px bg-n-strong" />

          <div class="flex items-center gap-2">
            <Button
              sm
              ghost
              slate
              icon="i-lucide-alarm-clock-plus"
              :label="t('BULK_ACTION.CONTACTS.FOLLOW_UP.BUTTON')"
              class="!px-2"
              @click="openBulkFollowUp"
            />
          </div>
        </template>

        <div class="h-4 w-px bg-n-strong" />

        <div class="flex items-center gap-2">
          <Button
            sm
            ghost
            ruby
            icon="i-lucide-trash-2"
            :label="t('BULK_ACTION.CONTACTS.DELETE.BUTTON')"
            :is-loading="isApplying"
            class="!px-2"
            @click="openDeleteDialog"
          />
        </div>

        <div class="h-4 w-px bg-n-strong" />

        <Button
          sm
          ghost
          slate
          icon="i-lucide-x"
          :label="t('BULK_ACTION.CONTACTS.CLEAR_SELECTION')"
          class="!px-2"
          @click="clearSelection"
        />
      </div>
    </transition>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="
        t('BULK_ACTION.CONTACTS.DELETE.DIALOG.TITLE', { count: selectedCount })
      "
      :description="t('BULK_ACTION.CONTACTS.DELETE.DIALOG.DESCRIPTION')"
      :confirm-button-label="t('BULK_ACTION.CONTACTS.DELETE.DIALOG.CONFIRM')"
      :is-loading="isApplying"
      @confirm="handleBulkDelete"
    />

    <transition
      enter-active-class="transition-all duration-150 ease-out"
      leave-active-class="transition-all duration-150 ease-out"
      enter-from-class="opacity-0 -translate-y-1"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <div
        v-if="showSelectAllBanner"
        class="flex items-center justify-center gap-1.5 py-2 px-4 rounded-lg bg-n-alpha-1 text-sm text-n-slate-11"
      >
        <span>
          {{
            t('BULK_ACTION.CONTACTS.PAGE_SELECTED', {
              count: props.contacts.length,
            })
          }}
        </span>
        <button
          type="button"
          class="font-medium text-n-brand hover:underline disabled:opacity-50"
          :disabled="isFetchingAll"
          @click="selectAllInView"
        >
          {{
            isFetchingAll
              ? t('BULK_ACTION.CONTACTS.SELECTING_ALL')
              : t('BULK_ACTION.CONTACTS.SELECT_ALL_IN_VIEW', {
                  count: props.totalContacts,
                })
          }}
        </button>
      </div>
    </transition>

    <div
      v-if="followUpLookupFailed"
      class="flex items-center justify-center gap-1.5 py-2 px-4 rounded-lg bg-n-alpha-1 text-sm text-n-slate-11"
    >
      <span>{{ t('CONTACTS_LAYOUT.FOLLOW_UP_LOOKUP.FAILED') }}</span>
      <button
        type="button"
        class="font-medium text-n-brand hover:underline"
        @click="retryFollowUpLookup"
      >
        {{ t('CONTACTS_LAYOUT.FOLLOW_UP_LOOKUP.RETRY') }}
      </button>
    </div>

    <ContactsCard
      v-for="contact in contacts"
      :id="contact.id"
      :key="contact.id"
      :name="contact.name"
      :email="contact.email"
      :thumbnail="contact.thumbnail"
      :phone-number="contact.phoneNumber"
      :additional-attributes="contact.additionalAttributes"
      :availability-status="contact.availabilityStatus"
      :is-expanded="expandedCardId === contact.id"
      :is-updating="isUpdating"
      :selectable="selectableCardIds.has(contact.id)"
      :is-selected="selectedContactIds.has(contact.id)"
      :show-follow-up="isFollowUpEnabled"
      :follow-up-enrollment="followUpByContactId[contact.id] || null"
      :follow-up-state-known="followUpStateKnown"
      @toggle="toggleExpanded(contact.id)"
      @update-contact="updateContact"
      @show-contact="onClickViewDetails"
      @add-to-flow="handleAddToFlow"
      @add-to-follow-up="handleAddToFollowUp"
      @edit-follow-up="handleEditFollowUp"
      @select="toggleContact"
      @mouseenter="hoveredCardId = contact.id"
      @mouseleave="hoveredCardId = null"
    />
    <WhatsappFlowEnroll
      v-if="showFlowEnrollModal && selectedContactId"
      :contact-id="selectedContactId"
      @close="closeFlowEnrollModal"
    />
    <FollowUpEnrollDialog
      v-if="isFollowUpEnabled"
      ref="followUpEnrollDialogRef"
      :contact-ids="followUpContactIds"
      @completed="refreshFollowUpsAfterMutation"
    />
    <FollowUpEditDialog
      v-if="isFollowUpEnabled"
      ref="followUpEditDialogRef"
      :enrollment="editingFollowUp"
      @changed="refreshFollowUpsAfterMutation"
    />
  </div>

  <Teleport to="body">
    <ContactBulkLabelActions
      v-if="showLabelActions"
      :style="dropdownStyle"
      @apply="handleApplyLabels"
      @close="showLabelActions = false"
    />
  </Teleport>
</template>
